import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Avatar,
  IconButton,
  TextField,
  Button,
  Alert,
  Grid,
  CircularProgress,
  Snackbar,
  Typography,
  Divider,
} from '@mui/material';
import { Close, PhotoCamera, Schedule, Language, Straighten } from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '@/hooks/redux';
import {
  updateUserProfileAsync,
  changePasswordAsync,
  clearProfileUpdateState,
  clearPasswordChangeState,
  uploadPhotoAsync,
  selectAuth,
} from '@/store/slices/authSlice';
import TimezoneSelector from '@/components/settings/TimezoneSelector';
import LanguageSwitcher from '@/components/settings/LanguageSwitcher';
import UnitPreferencesForm from '@/components/settings/UnitPreferencesForm';
import { getAvatarSrc, getInitials } from '@/utils/avatar';
import AvatarCropDialog from '@/components/user/AvatarCropDialog';

interface ProfileFormData {
  display_name?: string;
  name?: string;
  surname?: string;
  mobile_phone?: string;
}

interface PasswordFormData {
  current_password: string;
  new_password: string;
  confirm_password: string;
}

interface ProfileDialogProps {
  open: boolean;
  onClose: () => void;
}

const ProfileDialog: React.FC<ProfileDialogProps> = ({ open, onClose }) => {
  const { t } = useTranslation(['profile', 'forms', 'common']);
  const dispatch = useAppDispatch();
  const {
    user,
    profileUpdateLoading,
    profileUpdateError,
    profileUpdateSuccess,
    passwordChangeLoading,
    passwordChangeError,
    passwordChangeSuccess,
  } = useAppSelector(selectAuth);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [photoError, setPhotoError] = useState<string | null>(null);
  const [photoSuccess, setPhotoSuccess] = useState(false);

  // Crop dialog state
  const [cropSrc, setCropSrc] = useState<string | null>(null);

  const profileSchema = yup.object({
    display_name: yup.string().optional().max(100, t('profile:validation.displayNameMax')),
    name: yup.string().optional().max(50, t('profile:validation.nameMax')),
    surname: yup.string().optional().max(50, t('profile:validation.surnameMax')),
    mobile_phone: yup
      .string()
      .transform((v) => (v === '' ? undefined : v))
      .optional()
      .matches(/^[+]?[\d\s\-\(\)]+$/, t('profile:validation.phoneInvalid')),
  });

  const passwordSchema = yup.object({
    current_password: yup.string().required(t('profile:validation.currentPasswordRequired')),
    new_password: yup
      .string()
      .required(t('profile:validation.newPasswordRequired'))
      .min(8, t('profile:validation.passwordMin'))
      .matches(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/, t('profile:validation.passwordMatch')),
    confirm_password: yup
      .string()
      .oneOf([yup.ref('new_password')], t('profile:validation.passwordsMustMatch'))
      .required(t('profile:validation.passwordConfirmRequired')),
  });

  const {
    control: profileControl,
    handleSubmit: handleProfileSubmit,
    reset: resetProfileForm,
    formState: { errors: profileErrors },
  } = useForm<ProfileFormData>({
    resolver: yupResolver(profileSchema),
    defaultValues: {
      display_name: user?.display_name || '',
      name: user?.name || '',
      surname: user?.surname || '',
      mobile_phone: user?.mobile_phone || '',
    },
  });

  const {
    control: passwordControl,
    handleSubmit: handlePasswordSubmit,
    reset: resetPasswordForm,
    formState: { errors: passwordErrors },
  } = useForm<PasswordFormData>({
    resolver: yupResolver(passwordSchema),
    defaultValues: { current_password: '', new_password: '', confirm_password: '' },
  });

  useEffect(() => {
    if (user) {
      resetProfileForm({
        display_name: user.display_name || '',
        name: user.name || '',
        surname: user.surname || '',
        mobile_phone: user.mobile_phone || '',
      });
    }
  }, [user, resetProfileForm]);

  const onProfileSubmit = async (data: ProfileFormData) => {
    if (!user) return;
    await dispatch(updateUserProfileAsync({ userId: user.id, userData: data }));
  };

  const onPasswordSubmit = async (data: PasswordFormData) => {
    if (!user) return;
    await dispatch(
      changePasswordAsync({ current_password: data.current_password, new_password: data.new_password })
    );
    resetPasswordForm();
  };

  // Step 1: file selected → open crop dialog
  const handleFileSelected = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const objectUrl = URL.createObjectURL(file);
    setCropSrc(objectUrl);
    // Reset input so selecting the same file again still triggers onChange
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  // Step 2: crop confirmed → upload cropped blob
  const handleCropConfirmed = useCallback(async (blob: Blob) => {
    setCropSrc(null);
    setPhotoError(null);
    setPhotoUploading(true);
    const croppedFile = new File([blob], 'avatar.jpg', { type: 'image/jpeg' });
    const result = await dispatch(uploadPhotoAsync(croppedFile));
    setPhotoUploading(false);
    if ((result as any).success) {
      setPhotoSuccess(true);
    } else {
      setPhotoError((result as any).error || t('profile:photoError'));
    }
  }, [dispatch, t]);

  const handleCropCancelled = useCallback(() => {
    if (cropSrc) URL.revokeObjectURL(cropSrc);
    setCropSrc(null);
  }, [cropSrc]);

  const handleCloseSnackbar = () => {
    dispatch(clearProfileUpdateState());
    dispatch(clearPasswordChangeState());
    setPhotoSuccess(false);
  };

  const photoUrl = getAvatarSrc(user?.photo_url);
  const initials = getInitials(user?.display_name, user?.name, user?.email);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        {t('profile:title')}
        <IconButton
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
          size="small"
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ pt: 2 }}>
        {/* Avatar section */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
          <Box sx={{ position: 'relative' }}>
            <Avatar src={photoUrl} sx={{ width: 80, height: 80, fontSize: '2rem' }}>
              {initials}
            </Avatar>
            <IconButton
              size="small"
              disabled={photoUploading}
              sx={{
                position: 'absolute',
                bottom: 0,
                right: 0,
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                '&:hover': { bgcolor: 'action.hover' },
              }}
              onClick={() => fileInputRef.current?.click()}
            >
              {photoUploading ? (
                <CircularProgress size={16} />
              ) : (
                <PhotoCamera fontSize="small" />
              )}
            </IconButton>
            <input
              ref={fileInputRef}
              type="file"
              hidden
              accept="image/jpeg,image/png,image/webp"
              onChange={handleFileSelected}
            />
          </Box>
        </Box>

        {photoError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {photoError}
          </Alert>
        )}

        {/* Personal info form */}
        <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
          {t('profile:personalInfo')}
        </Typography>

        {profileUpdateError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {profileUpdateError}
          </Alert>
        )}

        <Box component="form" onSubmit={handleProfileSubmit(onProfileSubmit)} sx={{ mb: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Controller
                name="display_name"
                control={profileControl}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label={t('profile:displayName')}
                    error={!!profileErrors.display_name}
                    helperText={profileErrors.display_name?.message}
                    disabled={profileUpdateLoading}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="name"
                control={profileControl}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label={t('profile:firstName')}
                    error={!!profileErrors.name}
                    helperText={profileErrors.name?.message}
                    disabled={profileUpdateLoading}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="surname"
                control={profileControl}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label={t('profile:lastName')}
                    error={!!profileErrors.surname}
                    helperText={profileErrors.surname?.message}
                    disabled={profileUpdateLoading}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="mobile_phone"
                control={profileControl}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label={t('profile:phoneNumber')}
                    placeholder={t('profile:phonePlaceholder')}
                    error={!!profileErrors.mobile_phone}
                    helperText={profileErrors.mobile_phone?.message}
                    disabled={profileUpdateLoading}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Button
                type="submit"
                variant="contained"
                disabled={profileUpdateLoading}
                startIcon={profileUpdateLoading ? <CircularProgress size={20} /> : null}
              >
                {profileUpdateLoading ? t('profile:updating') : t('profile:saveAll')}
              </Button>
            </Grid>
          </Grid>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Timezone */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Schedule color="primary" sx={{ mr: 1 }} />
          <Typography variant="subtitle1" fontWeight="medium">
            {t('profile:timezonePreference')}
          </Typography>
        </Box>
        <TimezoneSelector />

        <Divider sx={{ my: 2 }} />

        {/* Language */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Language color="primary" sx={{ mr: 1 }} />
          <Typography variant="subtitle1" fontWeight="medium">
            {t('profile:languagePreference')}
          </Typography>
        </Box>
        <LanguageSwitcher variant="outlined" size="medium" fullWidth />

        <Divider sx={{ my: 2 }} />

        {/* Units */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Straighten color="primary" sx={{ mr: 1 }} />
          <Typography variant="subtitle1" fontWeight="medium">
            {t('profile:unitPreferences')}
          </Typography>
        </Box>
        <UnitPreferencesForm showTitle={false} />

        <Divider sx={{ my: 2 }} />

        {/* Change password */}
        <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
          {t('profile:changePassword')}
        </Typography>

        {passwordChangeError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {passwordChangeError}
          </Alert>
        )}

        <Box component="form" onSubmit={handlePasswordSubmit(onPasswordSubmit)}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Controller
                name="current_password"
                control={passwordControl}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    type="password"
                    label={t('profile:currentPassword')}
                    error={!!passwordErrors.current_password}
                    helperText={passwordErrors.current_password?.message}
                    disabled={passwordChangeLoading}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="new_password"
                control={passwordControl}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    type="password"
                    label={t('profile:newPassword')}
                    error={!!passwordErrors.new_password}
                    helperText={passwordErrors.new_password?.message}
                    disabled={passwordChangeLoading}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="confirm_password"
                control={passwordControl}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    type="password"
                    label={t('profile:confirmNewPassword')}
                    error={!!passwordErrors.confirm_password}
                    helperText={passwordErrors.confirm_password?.message}
                    disabled={passwordChangeLoading}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Button
                type="submit"
                variant="outlined"
                disabled={passwordChangeLoading}
                startIcon={passwordChangeLoading ? <CircularProgress size={20} /> : null}
              >
                {passwordChangeLoading
                  ? t('profile:changingPassword')
                  : t('profile:changePasswordButton')}
              </Button>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>

      {cropSrc && (
        <AvatarCropDialog
          open={Boolean(cropSrc)}
          imageSrc={cropSrc}
          onClose={handleCropCancelled}
          onCropComplete={handleCropConfirmed}
        />
      )}

      <Snackbar
        open={profileUpdateSuccess || passwordChangeSuccess || photoSuccess}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        message={
          photoSuccess
            ? t('profile:photoUpdated')
            : profileUpdateSuccess
            ? t('profile:success.profileUpdated')
            : t('profile:success.passwordChanged')
        }
      />
    </Dialog>
  );
};

export default ProfileDialog;
